import React, { useMemo } from 'react';
import AbrdnButton from '@/components/Generic/Buttons/AbrdnButton';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import useHelpers from 'hooks/helpers';
import { ComponentRendering, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import AbrdnPlaceholders from 'components/Generic/SitecoreWrappers/AbrdnPlaceholders';
import { useI18n } from 'next-localization';

type Rendering = ComponentRendering & {
  uid: string;
  placeholders: { [key: string]: { fields?: { fundInFocus?: boolean } }[] };
};

type Fields = {
  fundsLandingPage?: Field<string>;
};

type FundsInFocusProps = {
  fields?: Fields;
  rendering: Rendering;
};

const cloneRendering = (rendering: Rendering, isExperienceEditor: boolean): Rendering => {
  if (
    isExperienceEditor ||
    !rendering.placeholders['abrdn-fund-in-focus'] ||
    rendering.placeholders['abrdn-fund-in-focus'].length === 0
  ) {
    return rendering;
  }

  const clonedRendering = JSON.parse(JSON.stringify(rendering)) as Rendering;
  clonedRendering.placeholders['abrdn-fund-in-focus'] = clonedRendering.placeholders[
    'abrdn-fund-in-focus'
  ].filter((fund: any) => fund.fields?.fundInFocus);

  return clonedRendering;
};

const FundsInFocus: React.FC<FundsInFocusProps> = ({ fields = {}, rendering }) => {
  const {
    sitecoreContext: { pageEditing = false },
  } = useSitecoreContext();
  const { t } = useI18n();
  const clonedRendering = useMemo(
    () => cloneRendering(rendering, pageEditing),
    [rendering, pageEditing]
  );

  const hasAnyFund = !!clonedRendering.placeholders['abrdn-fund-in-focus']?.length;

  const viewMoreLink = useMemo(() => {
    if (!fields.fundsLandingPage?.value) return null;
    return {
      value: {
        href: fields.fundsLandingPage.value,
        text: '',
        linktype: '',
        url: '',
        class: '',
      },
    };
  }, [fields.fundsLandingPage]);

  const { backgroundClass } = useHelpers(rendering);

  return (
    <section
      id={rendering.uid}
      data-e2e-component-uid={rendering.uid}
      className={`default-section-padding ${backgroundClass}`}
    >
      <div className="container">
        <div>
          <AbrdnPlaceholders
            rendering={rendering}
            name="abrdn-tiles-heading"
            className={hasAnyFund ? 'mb-4' : ''}
          />
        </div>
        <div>
          <AbrdnPlaceholders
            rendering={clonedRendering}
            name="abrdn-fund-in-focus"
            className="row margin-top-row"
          />
        </div>
        {viewMoreLink && (
          <AbrdnButton
            buttonType="primary"
            linkField={viewMoreLink}
            showExternalIcon={false}
            className="mt-4"
          >
            <span> {t('visitFundCentre')}</span>
            <i className="bi bi-arrow-right ms-2" aria-hidden="true" />
          </AbrdnButton>
        )}
      </div>
    </section>
  );
};

export default FundsInFocus;
